import React, { FC, useContext } from 'react';

import { TValueSize, ValueSemanticType } from './models';
import { ValueSimple } from './_Simple/ValueSimple';
import { Icon } from '../Icon';
import { toSafeText } from './utils';
import { Link } from '../Link';
import { Box } from '../Box';
import { Text } from '../Text';
import { TTextAlign, TTextSize } from '../Text/models';
import { AppContext } from '@src/providers/App';
import { ShowMore } from '@src/molecules/ShowMore';
import { JsonView } from '../JsonView';
import { IDefaultComponentProps } from '@src/models/props';
import { AnyType } from '@src/models/common';

interface IValueProps extends Omit<IDefaultComponentProps, 'children'> {
  value: AnyType;
  textAlign?: TTextAlign;
  size?: TValueSize;
  semanticType: ValueSemanticType;
}

// todo: Перенести в молекулы
export const Value: FC<IValueProps> = ({ value, semanticType, textAlign = 'left', size = 'primary', ...rest }) => {
  const { locale } = useContext(AppContext);

  if (value === null || value === undefined) {
    return <></>;
  }
  switch (semanticType) {
    case ValueSemanticType.NUMBER:
      // https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString#browser_compatibility
      return (
        <ValueSimple size={size} textAlign={textAlign}>
          {value.toLocaleString(locale)}
        </ValueSimple>
      );

    case ValueSemanticType.NUMBER_INT:
      return (
        <ValueSimple size={size} textAlign={textAlign}>
          {Number(Number(value).toFixed(0)).toLocaleString(locale)}
        </ValueSimple>
      );

    case ValueSemanticType.NUMBER_FRACTION_DIGITS_1:
      return (
        <ValueSimple size={size} textAlign={textAlign}>
          {value.toLocaleString(locale, { minimumFractionDigits: 1 })}
        </ValueSimple>
      );

    case ValueSemanticType.NUMBER_FRACTION_DIGITS_2:
      return (
        <ValueSimple size={size} textAlign={textAlign}>
          {value.toLocaleString(locale, { minimumFractionDigits: 2 })}
        </ValueSimple>
      );

    case ValueSemanticType.BOOLEAN:
      return (
        <Box alignItems={'center'}>
          {value ? (
            <>
              <Box marginRight={4}>
                <Icon size={'sm'} name={'check'} />
              </Box>
              <Text size={size} id={'yes'}>
                Да
              </Text>
            </>
          ) : (
            <>
              <Box>
                <Icon size={'sm'} name={'minus'} />
              </Box>
            </>
          )}
        </Box>
      );

    case ValueSemanticType.BOOLEAN_TWO_VARIANTS:
      return (
        <Box alignItems={'center'}>
          {value ? (
            <>
              <Box marginRight={4}>
                <Icon size={'sm'} name={'check'} />
              </Box>
              <Text size={size} id={'no'}>
                Да
              </Text>
            </>
          ) : (
            <>
              <Box marginRight={4}>
                <Icon size={'sm'} name={'minus'} />
              </Box>
              <Text size={size} id={'no'}>
                Нет
              </Text>
            </>
          )}
        </Box>
      );

    case ValueSemanticType.TEXT:
      return (
        <ValueSimple size={size} textAlign={textAlign}>
          {toSafeText(value)}
        </ValueSimple>
      );

    case ValueSemanticType.LONG_TEXT:
      const spaceIndex = String(value).lastIndexOf(' ', 50);
      if (spaceIndex === -1) {
        return (
          <ValueSimple size={size} textAlign={textAlign}>
            {toSafeText(value)}
          </ValueSimple>
        );
      }
      return (
        <ShowMore
          inline={true}
          htmlContent={value.substring(0, spaceIndex) + '<hr class="delimiter">' + value.substring(spaceIndex)}
        ></ShowMore>
      );

    case ValueSemanticType.DATE_TIME:
      return (
        <ValueSimple size={size} textAlign={textAlign} nowrap>
          {new Date(value).toLocaleString(locale, {
            weekday: 'short',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </ValueSimple>
      );

    case ValueSemanticType.DATE:
      return (
        <ValueSimple size={size} textAlign={textAlign} nowrap>
          {new Date(value).toLocaleString(locale, { year: 'numeric', month: 'numeric', day: 'numeric' })}
        </ValueSimple>
      );

    case ValueSemanticType.YEAR:
      return (
        <ValueSimple size={size} textAlign={textAlign} nowrap>
          {new Date(value).toLocaleString(locale, { year: 'numeric' })}
        </ValueSimple>
      );

    case ValueSemanticType.YEAR_MONTH:
      return (
        <ValueSimple size={size} textAlign={textAlign} nowrap>
          {new Date(value).toLocaleString(locale, { year: 'numeric', month: 'long' })}
        </ValueSimple>
      );

    case ValueSemanticType.YEAR_MONTH_DAY:
      return (
        <ValueSimple size={size} textAlign={textAlign} nowrap>
          {new Date(value).toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric' })}
        </ValueSimple>
      );

    case ValueSemanticType.DAY_OF_WEEK:
      return (
        <ValueSimple size={size} textAlign={textAlign} nowrap>
          {new Date(value).toLocaleString(locale, { weekday: 'long' })}
        </ValueSimple>
      );

    case ValueSemanticType.RECORD:
      // простой первичный вариант отобразить сложные данные
      return (
        <Box style={{ minWidth: 400 }}>
          <JsonView data={value} />
        </Box>
      );

    case ValueSemanticType.HTML:
      const htmlTextSizeMap: { [key: string]: TTextSize } = {
        primary: 'wysiwyg',
        secondary: 'wysiwygSecondary',
        caption: 'wysiwygCaption',
      };
      const htmlSize = htmlTextSizeMap[size];
      // тут надо научиться все чистить не не пускать то, что не договоримся
      return (
        <Text id="wysiwyg" size={htmlSize} tag={'div'}>
          <ShowMore htmlContent={value} />
        </Text>
      );

    case ValueSemanticType.EMAIL:
      return (
        <Link id={'email'} href={'mailto:' + value} target={'_blank'} {...rest}>
          <Text id={'emailText'} noOfLines={2}>
            {value}
          </Text>
        </Link>
      );

    case ValueSemanticType.URL:
      let renderLinkText: string = value;
      if (renderLinkText.length > 45) {
        renderLinkText =
          renderLinkText.substring(0, 20) +
          '...' +
          renderLinkText.substring(renderLinkText.length - 20, renderLinkText.length);
      }
      return (
        <Link id={'url'} href={value} target={'_blank'} {...rest}>
          <Text id={'urlText'} noOfLines={1}>
            {renderLinkText}
          </Text>
        </Link>
      );

    case ValueSemanticType.JSX:
      return value;
  }

  return <b>Unexpected semantic type [{semanticType}]</b>;
};
